import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileTreeNode } from '@app-types/notes';
import { MoreVerticalIcon } from '@components/common/ui/icons';
import { useSidebarStore, useAuthStore } from '@hooks/useStores';
import { useToastContext } from '@contexts/ToastContext';
import { useModal } from '@hooks/useModal';
import { Modal } from '@components/common/ui/Modal';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import $api from '@http';
import { useDropdownPlacement } from '@hooks/useDropdownPlacement';
import * as styles from '@components/sidebar/FileSidebar.module.css';

const cn = (...classes: (string | undefined | false)[]) => classes.filter(Boolean).join(' ');

interface TreeNodeMenuProps {
    node: FileTreeNode;
    isOpen: boolean;
    onToggle: () => void;
    onClose: () => void;
    onDelete?: () => void;
}

export const TreeNodeMenu: React.FC<TreeNodeMenuProps> = observer(
    ({ node, isOpen, onToggle, onClose, onDelete }) => {
        const { t } = useTranslation();
        const wrapperRef = useRef<HTMLDivElement>(null);
        const placement = useDropdownPlacement(wrapperRef, isOpen, 220);
        const sidebarStore = useSidebarStore();
        const authStore = useAuthStore();
        const toast = useToastContext();
        const navigate = useNavigate();
        const { modalState, showModal, closeModal } = useModal();
        const isFolder = node.type === 'folder';
        const isShared = node.isShared ?? false;
        const isActivated = authStore.user?.isActivated ?? false;

        const handleRename = (e: React.MouseEvent) => {
            e.stopPropagation();
            onClose();
            if (!isActivated) {
                toast.warning(t('noteMenu.activateEdit'));
                return;
            }
            sidebarStore.startEditing(node.id, 'rename');
        };

        const handleCreateFolder = (e: React.MouseEvent) => {
            e.stopPropagation();
            onClose();
            if (!isActivated) {
                toast.warning(t('noteMenu.activateCreateFolders'));
                return;
            }
            sidebarStore.startEditing(`temp-folder-${Date.now()}`, 'create-folder', node.id);
        };

        const handleCreateNote = (e: React.MouseEvent) => {
            e.stopPropagation();
            onClose();
            if (!isActivated) {
                toast.warning(t('noteMenu.activateCreateNotes'));
                return;
            }
            sidebarStore.startEditing(`temp-note-${Date.now()}`, 'create-note', node.id);
        };

        const handleCreateSubnote = (e: React.MouseEvent) => {
            e.stopPropagation();
            onClose();
            if (!isActivated) {
                toast.warning(t('noteMenu.activateCreateNotes'));
                return;
            }
            sidebarStore.startEditing(`temp-subnote-${Date.now()}`, 'create-subnote', node.id);
        };

        const handleTogglePin = async (e: React.MouseEvent) => {
            e.stopPropagation();
            onClose();
            try {
                const res = await $api.patch(`/notes/${node.id}/pin`);
                sidebarStore.togglePinNode(node.id, res.data.isPinned);
                toast.success(res.data.isPinned ? t('noteMenu.pinned') : t('noteMenu.unpinned'));
            } catch {
                // error toast handled by axios interceptor
            }
        };

        const handleDelete = (e: React.MouseEvent) => {
            e.stopPropagation();
            onClose();
            if (!isActivated) {
                toast.warning(t('noteMenu.activateDelete'));
                return;
            }
            e.stopPropagation();
            onClose();

            showModal(
                `${t('noteMenu.delete')} ${isFolder ? t('topbar.folderLabel').toLowerCase() : t('topbar.noteLabel').toLowerCase()}`,
                t('topbar.deleteForeverMessage', {
                    type: isFolder ? t('topbar.folderLabel') : t('topbar.noteLabel'),
                    title: node.name,
                    extra: '',
                }),
                async () => {
                    try {
                        if (isFolder) {
                            await $api.delete(`/folders/${node.id}`);
                        } else {
                            await $api.delete(`/notes/${node.id}`);
                        }

                        sidebarStore.deleteNode(node.id);
                        window.dispatchEvent(new CustomEvent('trash:updated'));
                        toast.success(`${isFolder ? t('topbar.folderLabel') : t('topbar.noteLabel')} ${t('noteMenu.delete').toLowerCase()}`);

                        if (onDelete) {
                            onDelete();
                        }
                    } catch (err: any) {
                        console.error(`Failed to delete ${isFolder ? 'folder' : 'note'}:`, err);
                        // Error toast is handled by axios interceptor
                    }
                },
                {
                    confirmText: t('topbar.deleteForeverConfirm'),
                    cancelText: t('topbar.cancel'),
                    variant: 'danger',
                },
            );
        };

        const handleLeave = (e: React.MouseEvent) => {
            e.stopPropagation();
            onClose();
            showModal(
                t('noteMenu.leaveNote'),
                t('noteMenu.leaveNoteConfirm', { title: node.name }),
                async () => {
                    try {
                        await $api.delete(`/notes/${node.id}/access/me`);
                        sidebarStore.removeSharedNote(node.id);
                        toast.success(t('noteMenu.leftNote'));
                        // Navigate away if currently viewing this note
                        if (window.location.pathname === `/note/${node.id}`) {
                            navigate('/');
                        }
                    } catch (err: any) {
                        console.error('Failed to leave note:', err);
                    }
                },
                {
                    confirmText: t('noteMenu.leaveNote'),
                    cancelText: t('topbar.cancel'),
                    variant: 'danger',
                },
            );
        };

        return (
            <>
                <div className={styles.dropdown} ref={wrapperRef}>
                    <button
                        className={styles.dropdownTrigger}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle();
                        }}
                    >
                        <MoreVerticalIcon className={styles.iconSmall} />
                    </button>
                    {isOpen && (
                        <div className={cn(styles.dropdownMenu, placement === 'top' && styles.dropdownMenuUp)}>
                            {isShared ? (
                                <button
                                    className={cn(styles.dropdownItem, styles.dropdownItemDanger)}
                                    onClick={handleLeave}
                                >
                                    {t('noteMenu.leaveNote')}
                                </button>
                            ) : (
                                <>
                                    <button className={styles.dropdownItem} onClick={handleRename}>
                                        {t('noteMenu.rename')}
                                    </button>

                                    {isFolder ? (
                                        <>
                                            <button
                                                className={styles.dropdownItem}
                                                onClick={handleCreateFolder}
                                            >
                                                {t('noteMenu.createFolder')}
                                            </button>
                                            <button
                                                className={styles.dropdownItem}
                                                onClick={handleCreateNote}
                                            >
                                                {t('noteMenu.createNote')}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                className={styles.dropdownItem}
                                                onClick={handleCreateSubnote}
                                            >
                                                {t('noteMenu.createSubnote')}
                                            </button>
                                            <button
                                                className={styles.dropdownItem}
                                                onClick={handleTogglePin}
                                            >
                                                {node.isPinned ? t('noteMenu.unpin') : t('noteMenu.pin')}
                                            </button>
                                        </>
                                    )}

                                    <div className={styles.dropdownDivider} />
                                    <button
                                        className={cn(styles.dropdownItem, styles.dropdownItemDanger)}
                                        onClick={handleDelete}
                                    >
                                        {t('noteMenu.delete')}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
                {modalState && (
                    <Modal
                        isOpen={modalState.isOpen}
                        onClose={closeModal}
                        title={modalState.title}
                        message={modalState.message}
                        confirmText={modalState.confirmText}
                        cancelText={modalState.cancelText}
                        onConfirm={modalState.onConfirm}
                        variant={modalState.variant}
                    />
                )}
            </>
        );
    },
);
